import { SIGNUS_API_URL } from './constants';
import { ApiError, extractErrorMessage, extractErrorType, fetchWithTokenRefresh } from './apiFetch';
import { Question } from './agreementTypes';

export enum WordDraftGeneratorDocumentType {
  NDA = 'NDA',
  EMPLOYMENT = 'EMPLOYMENT',
  CONTRACTOR = 'CONTRACTOR',
  PARTNERSHIP = 'PARTNERSHIP',
  SALES_AND_PURCHASE = 'SALES_AND_PURCHASE',
  SERVICE_AGREEMENT = 'SERVICE_AGREEMENT',
  LEASE_RENTAL = 'LEASE_RENTAL',
  IP_ASSIGNMENT = 'IP_ASSIGNMENT',
  NON_COMPETE = 'NON_COMPETE',
  MOU_LOI = 'MOU_LOI',
  CUSTOM = 'CUSTOM',
}

const DOCUMENT_TYPE_BY_AGREEMENT_ID: Record<string, WordDraftGeneratorDocumentType> = {
  nda: WordDraftGeneratorDocumentType.NDA,
  employment: WordDraftGeneratorDocumentType.EMPLOYMENT,
  contractor: WordDraftGeneratorDocumentType.CONTRACTOR,
  partnership: WordDraftGeneratorDocumentType.PARTNERSHIP,
  'sales-purchase': WordDraftGeneratorDocumentType.SALES_AND_PURCHASE,
  service: WordDraftGeneratorDocumentType.SERVICE_AGREEMENT,
  lease: WordDraftGeneratorDocumentType.LEASE_RENTAL,
  'ip-assignment': WordDraftGeneratorDocumentType.IP_ASSIGNMENT,
  'non-compete': WordDraftGeneratorDocumentType.NON_COMPETE,
  'mou-loi': WordDraftGeneratorDocumentType.MOU_LOI,
};

export function documentTypeForAgreementId(id: string): WordDraftGeneratorDocumentType {
  return DOCUMENT_TYPE_BY_AGREEMENT_ID[id] ?? WordDraftGeneratorDocumentType.CUSTOM;
}

interface WordDraftQuestionDto {
  sortingIndex: number;
  question: string;
  answerOptions?: string[] | null;
  isAaaClause?: boolean | null;
}
const FINAL_CATCH_ALL_QUESTION_PREFIX = "is there any additional information you'd like to include";

function isFinalCatchAllQuestion(question: string): boolean {
  return question.trim().toLowerCase().startsWith(FINAL_CATCH_ALL_QUESTION_PREFIX);
}

function toQuestion(dto: WordDraftQuestionDto): Question {
  const hasOptions = !!dto.answerOptions && dto.answerOptions.length > 0;
  return {
    id: `q${dto.sortingIndex}`,
    prompt: dto.question,
    // The backend doesn't distinguish short vs. long free-text answers — use a
    // textarea by default so multi-sentence answers aren't cramped.
    type: hasOptions ? 'select' : 'longtext',
    options: hasOptions ? (dto.answerOptions as string[]) : undefined,
  };
}

export async function fetchDraftQuestions(
  teamSlug: string,
  accessToken: string,
  documentType: WordDraftGeneratorDocumentType,
  text?: string,
): Promise<Question[]> {
  const url = `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/draft/questions`;

  const res = await fetchWithTokenRefresh(url, accessToken, token => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify({ documentType, text }),
  }));

  const json = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(
      extractErrorMessage(json, `Failed to fetch questions: ${res.status} ${res.statusText}`),
      extractErrorType(json),
    );
  }

  const dtos: WordDraftQuestionDto[] = json?.payload?.questions ?? [];
  return dtos
    .filter(dto => !isFinalCatchAllQuestion(dto.question))
    .sort((a, b) => a.sortingIndex - b.sortingIndex)
    .map(toQuestion);
}
